import React, { useEffect, useRef, useState } from "react";
import { useSearchParams, Link } from "react-router-dom";
import { Search, Sparkles } from "lucide-react";
import { supabase } from "../../lib/supabase";
import { getApiBase } from "../../lib/api";

export type Product = {
  id: string;
  name: string;
  description: string;
  price: number;
  image_url: string;
  brandId: number;
  categoryId: number;
  quantity?: number;
  weight: number;
  length: number;
  width: number;
  height: number;
  is_active?: boolean;
  is_featured?: boolean;
};

type AiMatch = {
  product: Product;
  reason: string;
  score: number;
};

const getProductIdsForAttributeSlug = async (slug: string) => {
  const { data: attribute, error: attributeError } = await supabase
    .from("attributes")
    .select("id")
    .eq("slug", slug)
    .single();

  if (attributeError || !attribute) {
    throw attributeError || new Error("Attribute not found.");
  }

  const { data: productAttributes, error: productAttributesError } = await supabase
    .from("product_attributes")
    .select("product_id")
    .eq("attribute_id", attribute.id);

  if (productAttributesError) {
    throw productAttributesError;
  }

  return Array.from(
    new Set((productAttributes || []).map((row: any) => row.product_id))
  );
};

const getProductIdsForCategory = async (categoryId: number) => {
  const { data, error } = await supabase
    .from("product_categories")
    .select("product_id")
    .eq("category_id", categoryId);

  if (error) {
    throw error;
  }

  return Array.from(new Set((data || []).map((row: any) => row.product_id)));
};

const ProductList = () => {
  const [searchParams] = useSearchParams();
  const [products, setProducts] = useState<Product[]>([]);
  const [nameQuery, setNameQuery] = useState("");
  const [aiQuery, setAiQuery] = useState("");
  const [aiMatches, setAiMatches] = useState<AiMatch[]>([]);
  const [aiLoading, setAiLoading] = useState(false);
  const [aiError, setAiError] = useState<string | null>(null);
  const [showAiSearch, setShowAiSearch] = useState(false);
  const aiInputRef = useRef<HTMLInputElement | null>(null);

  useEffect(() => {
    const fetchProducts = async () => {
      const categoryId = searchParams.get("category");
      const categoryIdAlt = searchParams.get("categoryId");
      const showAll = searchParams.get("all");
      const brandId = searchParams.get("brand");
      const hairType = searchParams.get("hairType");
      const skinType = searchParams.get("skinType");

      let query = supabase.from("Product").select("*");

      if (hairType || skinType) {
        try {
          const productIds = await getProductIdsForAttributeSlug(
            String(hairType || skinType)
          );

          if (productIds.length === 0) {
            setProducts([]);
            return;
          }

          query = query.in("id", productIds);
        } catch (attributeFilterError) {
          console.error("Error fetching attribute products:", attributeFilterError);
          setProducts([]);
          return;
        }
      } else if (brandId) {
        query = query.eq("brandId", Number(brandId));
      } else if (categoryId || categoryIdAlt) {
        const selectedCategoryId = Number(categoryId || categoryIdAlt);

        try {
          const linkedProductIds = await getProductIdsForCategory(selectedCategoryId);

          if (linkedProductIds.length > 0) {
            query = query.or(
              `categoryId.eq.${selectedCategoryId},id.in.(${linkedProductIds.join(",")})`
            );
          } else {
            query = query.eq("categoryId", selectedCategoryId);
          }
        } catch (categoryFilterError) {
          console.error("Error fetching category products:", categoryFilterError);
          query = query.eq("categoryId", selectedCategoryId);
        }
      }

      const { data, error } = await query;

      if (error) {
        console.error("Error fetching products:", error);
        return;
      }

      const activeOnly = (data || []).filter((p: any) => p.is_active !== false);

      if (showAll === "true" && activeOnly.length > 0) {
        const shuffled = [...activeOnly].sort(() => Math.random() - 0.5);
        setProducts(shuffled as Product[]);
      } else {
        setProducts(activeOnly as Product[]);
      }
    };

    fetchProducts();
  }, [searchParams]);

  useEffect(() => {
    if (searchParams.get("ai") === "true") {
      setShowAiSearch(true);
      window.setTimeout(() => aiInputRef.current?.focus(), 150);
    }
  }, [searchParams]);

  const visibleProducts = products.filter((product) =>
    product.name.toLowerCase().includes(nameQuery.trim().toLowerCase())
  );

  const handleAiSearch = async (e: React.FormEvent) => {
    e.preventDefault();

    const query = aiQuery.trim();
    if (query.length < 3) {
      setAiError("Tell us a little more about what you need.");
      return;
    }

    setAiLoading(true);
    setAiError(null);

    try {
      const res = await fetch(`${getApiBase()}/api/ai/product-search`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ query, limit: 6 }),
      });

      const responseText = await res.text();
      let data: any = null;

      try {
        data = responseText ? JSON.parse(responseText) : null;
      } catch {
        data = null;
      }

      if (!res.ok) {
        throw new Error(
          data?.error ||
            responseText ||
            `Could not find product matches. Server returned ${res.status}.`
        );
      }

      setAiMatches(data?.matches || []);
    } catch (err: any) {
      setAiError(err?.message || "Could not find product matches.");
      setAiMatches([]);
    } finally {
      setAiLoading(false);
    }
  };

  const renderProductCard = (product: Product, reason?: string) => (
    <Link to={`/product/${product.id}`} key={product.id}>
      <div className="border border-[rgba(92,47,47,0.2)] rounded-xl p-4 shadow-md hover:shadow-[0_8px_24px_rgba(92,47,47,0.3)] transition cursor-pointer h-full bg-white/90 backdrop-blur-md">
        <img
          src={product.image_url}
          alt={product.name}
          className="w-full h-96 object-contain mb-4 bg-transparent"
        />

        <h2 className="text-xl font-bold text-[#5c2f2f] mb-1">
          {product.name}
        </h2>

        <p className="text-base text-[#7a4343]">
          ${Number(product.price || 0).toFixed(2)}
        </p>

        {reason && (
          <p className="mt-3 rounded-lg bg-[#fff6f6] px-3 py-2 text-sm text-[#6f3a3a]">
            {reason}
          </p>
        )}
      </div>
    </Link>
  );

  return (
    <div className="p-8 w-full">
      <h1 className="text-3xl font-bold mb-6 text-[#5c2f2f]">Product List</h1>

      <section className="mb-8 rounded-2xl border border-[rgba(92,47,47,0.16)] bg-white/90 p-5 shadow-sm">
        <div className="grid gap-3 md:grid-cols-[1fr_auto]">
          <label className="relative block">
            <Search
              size={18}
              className="pointer-events-none absolute left-4 top-1/2 -translate-y-1/2 text-[#9a6a6a]"
              aria-hidden="true"
            />
            <input
              type="search"
              value={nameQuery}
              onChange={(e) => setNameQuery(e.target.value)}
              placeholder="Search products by name"
              className="min-h-[48px] w-full rounded-xl border border-[rgba(92,47,47,0.25)] px-11 text-[#5c2f2f] outline-none focus:ring-2 focus:ring-[#a25d5d]"
            />
          </label>
          <button
            type="button"
            onClick={() => {
              setShowAiSearch((prev) => !prev);
              window.setTimeout(() => aiInputRef.current?.focus(), 100);
            }}
            className="inline-flex min-h-[48px] items-center justify-center gap-2 rounded-xl border border-[#e8d4cc] bg-[#fff8f5] px-5 font-semibold text-[#5c2f2f] shadow-sm transition hover:bg-[#f7ebe6]"
            aria-expanded={showAiSearch}
          >
            <Sparkles
              size={18}
              className="motion-safe:animate-bounce"
              aria-hidden="true"
            />
            AI Search
          </button>
        </div>

        {showAiSearch && (
          <div className="mt-4 rounded-xl border border-[#ecd9d2] bg-[#fffaf8] p-4">
            <form
              onSubmit={handleAiSearch}
              className="grid gap-3 md:grid-cols-[1fr_auto]"
            >
              <input
                ref={aiInputRef}
                type="text"
                value={aiQuery}
                onChange={(e) => setAiQuery(e.target.value)}
                placeholder="Tell us what you need, like dry frizzy curly hair"
                className="min-h-[48px] rounded-xl border border-[rgba(92,47,47,0.25)] px-4 text-[#5c2f2f] outline-none focus:ring-2 focus:ring-[#a25d5d]"
              />
              <button
                type="submit"
                disabled={aiLoading}
                className={`min-h-[48px] rounded-xl px-5 font-semibold text-white transition ${
                  aiLoading
                    ? "bg-gray-300 cursor-not-allowed"
                    : "bg-[#5c2f2f] hover:bg-[#7a4444]"
                }`}
              >
                {aiLoading ? "Finding..." : "Find Matches"}
              </button>
            </form>
          </div>
        )}

        {aiError && <p className="mt-3 text-sm text-red-600">{aiError}</p>}

        {aiMatches.length > 0 && (
          <div className="mt-6">
            <h2 className="mb-4 text-xl font-semibold text-[#5c2f2f]">
              Recommended for You
            </h2>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
              {aiMatches.map((match) =>
                renderProductCard(match.product, match.reason)
              )}
            </div>
          </div>
        )}
      </section>

      {visibleProducts.length === 0 ? (
        <p className="text-[#7a4343]">No products found.</p>
      ) : (
        <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-10">
          {visibleProducts.map((product) => renderProductCard(product))}
        </div>
      )}
    </div>
  );
};

export default ProductList;
